import * as React from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { TotadminPermission } from 'data/__generated__';
import { useMissingPermissionsReason, usePermissionGate } from 'hooks/useHasPermission';
import { PermissionLockIcon } from 'components/PermissionLock';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type PageActionItem = {
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    to?: string;
    disabled?: boolean;
    variant?: 'default' | 'destructive';
    dataTest?: string;
    /** Droits fins TOUS requis. Sans eux : entrée grisée, cadenas, raison en infobulle. */
    requiredPermissions?: readonly TotadminPermission[];
};

export type PageActionGroup = {
    label?: string;
    items: PageActionItem[];
};

type Props = {
    label?: string;
    groups: PageActionGroup[];
    align?: 'start' | 'center' | 'end';
};

function PageActionMenuItem({ item }: { item: PageActionItem }) {
    const { isAllowed, missing } = usePermissionGate(item.requiredPermissions);
    const reason = useMissingPermissionsReason(missing);
    const disabled = item.disabled || !isAllowed;
    const content = (
        <>
            {isAllowed ? item.icon : <PermissionLockIcon />}
            <span>{item.label}</span>
        </>
    );

    if (item.to && !disabled) {
        return (
            <DropdownMenuItem asChild variant={item.variant}>
                <Link to={item.to} data-test={item.dataTest}>
                    {content}
                </Link>
            </DropdownMenuItem>
        );
    }

    return (
        <DropdownMenuItem
            onSelect={item.onClick}
            disabled={disabled}
            variant={item.variant}
            data-test={item.dataTest}
            title={reason ?? undefined}
        >
            {content}
        </DropdownMenuItem>
    );
}

export const PageActionsMenu = ({ label = 'Actions', groups, align = 'end' }: Props) => {
    const hasContent = groups.some((group) => group.items.length > 0);
    if (!hasContent) {
        return null;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button size="lg" variant="outline" data-test="page-actions-menu-trigger">
                    <MoreHorizontal />
                    {label}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align} className="min-w-56" data-test="page-actions-menu">
                {groups.map((group, groupIndex) => {
                    if (group.items.length === 0) {
                        return null;
                    }
                    return (
                        <React.Fragment key={groupIndex}>
                            {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                            {group.label ? <DropdownMenuLabel>{group.label}</DropdownMenuLabel> : null}
                            {group.items.map((item, itemIndex) => (
                                <PageActionMenuItem key={itemIndex} item={item} />
                            ))}
                        </React.Fragment>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
